import { SourceTypes } from '../../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../../utils';
import type { DataValue, ProviderType } from '../../types';
import type { InstagramSourceValue } from '../types';
import type { LegacyInstagramResponseType } from './types';
import { getInstagramMedia } from './utils';

const SOURCE_TYPE = SourceTypes.INSTAGRAM;

const REQUEST_FIELDS = [
  'caption',
  'media_type',
  'media_url',
  'permalink',
  'thumbnail_url',
  'timestamp',
  'username',
  'children{media_type,media_url,thumbnail_url}'
];

const select = (response: LegacyInstagramResponseType): DataValue[] => {
  handleResponseError(response, SOURCE_TYPE, 'data');

  return response.data.map(
    ({
      caption,
      id,
      media_type: mediaType,
      media_url: mediaUrl,
      thumbnail_url: thumbnailUrl,
      permalink,
      timestamp,
      username,
      children
    }) => {
      const text = formatText(caption, SOURCE_TYPE);
      const media = getInstagramMedia({
        media_type: mediaType,
        media_url: mediaUrl,
        thumbnail_url: thumbnailUrl,
        children
      });

      return {
        id,
        text,
        media,
        timestamp: Date.parse(timestamp),
        source: {
          type: SOURCE_TYPE,
          url: permalink
        },
        user: {
          username,
          url: `https://instagram.com/${username}`
        },
        counters: {
          likes: 0,
          comments: 0
        }
      };
    }
  );
};

export function createInstagramPersonalAccountProvider(
  { authentication }: InstagramSourceValue<'legacyInstagramPersonalAccount'>,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<LegacyInstagramResponseType> {
  const identity = authentication?.identity ?? '';
  const key = [...globalKey, ...filtersKey, identity];

  const page = (data: LegacyInstagramResponseType | undefined) => {
    if (data) {
      return data.paging?.next;
    }

    const requestParams = new URLSearchParams({
      fields: REQUEST_FIELDS.join(',')
    });
    const requestURL = `me/media?${requestParams}`;

    return buildProxyURL({
      apiSource: SOURCE_TYPE,
      params: { q: requestURL, user_id: identity }
    });
  };

  return { key, select, page };
}
